import { ORIGIN } from "@/constants/testIds";
import { MapPin } from "lucide-react";

/* eslint-disable react/no-unescaped-entities */

const TAGS = ["Origin-led", "Transparent", "Fair Value at Source", "African Modern"];

const STATES = [
  {
    id: "imo",
    name: "Imo State",
    body: "Lush rainforest soils and reliable rains in the southeast — a quiet powerhouse of Nigerian cashew cultivation.",
    testId: ORIGIN.tagImo,
  },
  {
    id: "abia",
    name: "Abia State",
    body: "Hilly terrain, deep red earth and farming co-operatives we work with to lift quality and fair pricing at origin.",
    testId: ORIGIN.tagAbia,
  },
];

const FARM_IMG =
  "https://static.prod-images.emergentagent.com/jobs/096b6207-302a-4d30-852c-60e212c167c7/images/648bde2139ae3ee9410807c3b76b80ede30e4e30ce19bad89b930085673b78d0.png";

export const Origin = () => {
  return (
    <section
      id="origin"
      data-testid={ORIGIN.wrapper}
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="relative rounded-[2.5rem] overflow-hidden bg-[#1a3626] shadow-[0_30px_80px_rgba(26,54,38,0.3)]">
          <div className="pointer-events-none absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#c5a059]/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 w-[360px] h-[360px] rounded-full bg-[#c86b5e]/10 blur-3xl" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c5a059]/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c5a059]/40 to-transparent" />

          <div className="grid lg:grid-cols-2 gap-0">
            <div className="p-8 sm:p-12 lg:p-16 relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-[#c5a059]" />
                <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#c5a059]">
                  Origin Pride
                </span>
              </div>
              <h2 className="font-serif-display text-[38px] sm:text-[52px] leading-[1.05] text-[#faf7f2] tracking-tight">
                Why Nigerian <br />
                <em className="italic text-[#c5a059]">Cashews?</em>
              </h2>
              <p className="mt-7 text-[15.5px] sm:text-[16.5px] leading-[1.85] text-[#faf7f2]/80 max-w-xl">
                Nigeria is one of Africa's most important cashew-growing
                countries — yet most of the value created from those kernels
                still leaves the continent before it ever ripens into a brand.
              </p>
              <p className="mt-4 text-[15.5px] sm:text-[16.5px] leading-[1.85] text-[#faf7f2]/80 max-w-xl">
                NAIJA Cashews works at the source in{" "}
                <span className="text-[#c5a059] font-medium">Imo</span> and{" "}
                <span className="text-[#c5a059] font-medium">Abia States</span>{" "}
                to process locally, pay fairly, and deliver premium quality
                directly to European tables. Origin pride, made tangible.
              </p>

              {/* States */}
              <div className="mt-9 grid sm:grid-cols-2 gap-4">
                {STATES.map((s) => (
                  <div
                    key={s.id}
                    data-testid={s.testId}
                    className="rounded-2xl border border-[#c5a059]/25 bg-[#c5a059]/5 p-5"
                  >
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#c5a059]">
                      <MapPin size={12} /> {s.name}
                    </div>
                    <p className="mt-2 text-[13.5px] leading-[1.7] text-[#faf7f2]/80">
                      {s.body}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-2.5">
                {TAGS.map((t, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-2 rounded-full border border-[#c5a059]/40 bg-[#c5a059]/5 px-4 py-2 text-[12px] tracking-wide text-[#faf7f2]"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#c5a059]" />
                    {t}
                  </span>
                ))}
              </div>

              <div className="mt-12 grid grid-cols-3 gap-6 pt-8 border-t border-[#c5a059]/20">
                {[
                  { v: "100%", l: "Nigerian Origin" },
                  { v: "0", l: "Additives" },
                  { v: "250g", l: "Per Pouch" },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="font-serif-display text-[30px] sm:text-[40px] text-[#c5a059] leading-none">
                      {s.v}
                    </div>
                    <div className="mt-2 text-[10.5px] uppercase tracking-[0.22em] text-[#faf7f2]/60">
                      {s.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative min-h-[320px] lg:min-h-[600px]">
              <img
                src={FARM_IMG}
                alt="Sunset over a Nigerian cashew farm"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#1a3626]/10 to-[#1a3626]/70 lg:bg-gradient-to-l lg:from-transparent lg:to-[#1a3626]/40" />
              <div className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8 bg-[#faf7f2] rounded-2xl px-5 py-4 shadow-[0_10px_30px_rgba(0,0,0,0.25)] flex items-center gap-3 max-w-[280px]">
                <div className="w-10 h-10 rounded-full bg-[#1a3626] text-[#c5a059] flex items-center justify-center">
                  <MapPin size={16} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#8b5a2b]">
                    Sourced From
                  </div>
                  <div className="font-serif-display text-[19px] text-[#1a3626] leading-tight">
                    Imo & Abia, Nigeria
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Farmer quote / value at origin */}
        <div className="mt-16 max-w-3xl mx-auto text-center">
          <div className="font-serif-display italic text-[26px] sm:text-[32px] leading-[1.3] text-[#1a3626]">
            "We don't just buy cashews. We invest in the hands that grow them
            — so more of the value, and the pride, stays at home."
          </div>
          <div className="mt-4 text-[11px] uppercase tracking-[0.28em] text-[#8b5a2b]">
            — Neal Chidi Emegwele, Founder
          </div>
        </div>
      </div>
    </section>
  );
};
